import colors from 'tailwindcss/colors';

export const neutral = {
  ...colors.neutral,
  750: '#333333',
  850: '#1E1E1E',
};

/* Radix UI Colors 3.0.0 — sRGB, light + dark
 * Used as primitives for entity-level semantic tokens. */

export const tomato = {
  1: '#fffcfc',
  2: '#fff8f7',
  3: '#feebe7',
  4: '#ffdcd3',
  5: '#ffcdc2',
  6: '#fdbdaf',
  7: '#f5a898',
  8: '#ec8e7b',
  9: '#e54d2e',
  10: '#dd4425',
  11: '#d13415',
  12: '#5c271f',
};

export const tomatoDark = {
  1: '#181111',
  2: '#1f1513',
  3: '#391714',
  4: '#4e1511',
  5: '#5e1c16',
  6: '#6e2920',
  7: '#853a2d',
  8: '#ac4d39',
  9: '#e54d2e',
  10: '#ec6142',
  11: '#ff977d',
  12: '#fbd3cb',
};

// maps to radix orange, changed from original to avoid tailwind conflicts
export const tangerine = {
  1: '#fefcfb',
  2: '#fff7ed',
  3: '#ffefd6',
  4: '#ffdfb5',
  5: '#ffd19a',
  6: '#ffc182',
  7: '#f5ae73',
  8: '#ec9455',
  9: '#f76b15',
  10: '#ef5f00',
  11: '#cc4e00',
  12: '#582d1d',
};

export const tangerineDark = {
  1: '#17120e',
  2: '#1e160f',
  3: '#331e0b',
  4: '#462100',
  5: '#562800',
  6: '#66350c',
  7: '#7e451d',
  8: '#a35829',
  9: '#f76b15',
  10: '#ff801f',
  11: '#ffa057',
  12: '#ffe0c2',
};

export const plum = {
  1: '#fefcff',
  2: '#fdf7fd',
  3: '#fbebfb',
  4: '#f7def8',
  5: '#f2d1f3',
  6: '#e9c2ec',
  7: '#deade3',
  8: '#cf91d8',
  9: '#ab4aba',
  10: '#a144af',
  11: '#953ea3',
  12: '#53195d',
};

export const plumDark = {
  1: '#181118',
  2: '#201320',
  3: '#351a35',
  4: '#451d47',
  5: '#512454',
  6: '#5e3061',
  7: '#734079',
  8: '#92549c',
  9: '#ab4aba',
  10: '#b658c4',
  11: '#e796f3',
  12: '#f4d4f4',
};

export const crimson = {
  1: '#fffcfd',
  2: '#fef7f9',
  3: '#ffe9f0',
  4: '#fedce7',
  5: '#facedd',
  6: '#f3bed1',
  7: '#eaacc3',
  8: '#e093b2',
  9: '#e93d82',
  10: '#df3478',
  11: '#cb1d63',
  12: '#621639',
};

export const crimsonDark = {
  1: '#191114',
  2: '#201318',
  3: '#381525',
  4: '#4d122f',
  5: '#5c1839',
  6: '#6d2545',
  7: '#873356',
  8: '#b0436e',
  9: '#e93d82',
  10: '#ee518a',
  11: '#ff92ad',
  12: '#fdd3e8',
};

/* Radix red — the locked destructive palette for the semantic validation-*
 * rework (DS-1202 / DS-1265). Exact Radix Colors 3.0.0 sRGB values. */

export const red = {
  1: '#fffcfc',
  2: '#fff7f7',
  3: '#feebec',
  4: '#ffdbdc',
  5: '#ffcdce',
  6: '#fdbdbe',
  7: '#f4a9aa',
  8: '#eb8e90',
  9: '#e5484d',
  10: '#dc3e42',
  11: '#ce2c31',
  12: '#641723',
};

export const redDark = {
  1: '#191111',
  2: '#201314',
  3: '#3b1219',
  4: '#500f1c',
  5: '#611623',
  6: '#72232d',
  7: '#8c333a',
  8: '#b54548',
  9: '#e5484d',
  10: '#ec5d5e',
  11: '#ff9592',
  12: '#ffd1d9',
};

export const brandPrimary = {
  50: '#EBEEFF',
  100: '#DDE2FF',
  200: '#ADB3E4',
  300: '#7275C5',
  400: '#4551A9',
  500: '#162694',
  600: '#12207B',
  700: '#0F1963',
};

export const brandAccent = {
  50: colors.violet[50],
  100: colors.violet[100],
  200: colors.violet[200],
  300: colors.violet[300],
  400: colors.violet[400],
  500: colors.violet[500],
  600: colors.violet[600],
  700: colors.violet[700],
};

export const sand = {
  15: '#FEFCFA',
  25: '#FCFAF8',
  50: '#F9F4F1',
  75: '#F6EFEA',
  100: '#F6ECE4',
  150: '#F4E7DD',
  200: '#F0DFD1',
  300: '#ECD7C6',
  400: '#E2CAB6',
  500: '#DABFA9',
  600: '#D1AF94',
  700: '#A68055',
  800: '#795B38',
  900: '#4D3826',
  950: '#2F2117',
  DEFAULT: '#F6ECE4',
};

export const bark = {
  25: '#FBF9FA',
  50: '#F4F0F2',
  100: '#E9E2E3',
  200: '#D4C4C9',
  300: '#C39DA8',
  400: '#A98993',
  500: '#876C74',
  600: '#755760',
  700: '#5B3E47',
  800: '#4C2934',
  900: '#28030F',
  950: '#211217',
  DEFAULT: '#28030F',
};

export const sunlight = {
  50: '#FEFDE8',
  100: '#FDFAC4',
  200: '#FBF582',
  300: '#FDF444',
  400: '#EBDF00',
  500: '#CCC200',
  600: '#999100',
  700: '#807900',
  800: '#666100',
  900: '#4D4900',
  950: '#2E2C05',
  DEFAULT: '#FBF582',
};

export const forest = {
  50: '#F2F7F2',
  100: '#DEEDE1',
  200: '#CCE1CE',
  300: '#A9CCAE',
  400: '#7CAC83',
  500: '#588F60',
  600: '#407648',
  700: '#2B6433',
  800: '#194B22',
  900: '#143C1A',
  950: '#0A1E0D',
  DEFAULT: '#194B22',
};

export const skies = {
  25: '#EBF2FF',
  50: '#E0EBFF',
  100: '#D1E0FF',
  200: '#B9CFFF',
  300: '#99B9FF',
  400: '#7AA4FF',
  500: '#5B8DF6',
  600: '#3C6DDD',
  700: '#2255C3',
  800: '#1343AA',
  900: '#072C7E',
  950: '#03194A',
  DEFAULT: '#B9CFFF',
};
